import TransactionRepository from '../repositories/TransactionRepository';
import { diContainer, serviceNames } from '../diContainer/diContainer';

export interface ITransactionLoad {
  transactionLoadOptions: ITransactionLoadOptions;
}

interface ITransactionLoadOptions {
  isEnabled?: boolean;
  isControlled?: boolean;
  shouldAwait?: boolean;
}

// Every use case extending this, will have the reloading transaction option enabled
const defaultOptions: ITransactionLoadOptions = {
  // let the concrete class define when to load the transaction
  isControlled: false,
  // enable transaction reloading
  isEnabled: true,
  // await for transaction reloading after use case execution
  shouldAwait: false,
};

// This abstract class is intentend to be used with use cases that requires transaction updates
// It helps encapsulating details needed to control a transaction reload
export default abstract class TransactionLoadHandler {

  private options: ITransactionLoadOptions;

  protected transactionRepository: TransactionRepository;

  // Static tracker to manage concurrent instances
  protected static lastRequestId = '';

  protected currentRequestId: string;

  constructor(options: ITransactionLoadOptions = defaultOptions) {
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.options = { ...defaultOptions, ...options };
    this.currentRequestId = this.generateRequestId();
  }

  protected abstract run(): Promise<void>;

  async handleTransactionUpdate(signal?: AbortSignal): Promise<void> {
    const {
      isEnabled,
      shouldAwait,
    } = this.options;
    if (isEnabled && this.transactionRepository.transaction.isDirty) {
      if (shouldAwait) {
        await this.transactionRepository.loadTransaction(undefined, signal);
      } else {
        this.transactionRepository.loadTransaction(undefined, signal);
      }
    }
  }

  async execute(): Promise<void> {
    await this.run();
    if (!this.options.isControlled) {
      await this.handleTransactionUpdate();
    }
  }

  /**
   * Generates a unique request ID to track concurrent executions
   */
  private generateRequestId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };
}